import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Pagination,
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { GridPagination } from '@mui/x-data-grid/components';
import localeText from 'utils/localeText';
import RowActions, { getRowActionsWidth } from './RowActions';
import { getColumnVisibilityModel, getSortModel, mergeColumns } from './utils';
import styles from './styles';

const pageSizeOptions = [10, 25, 50, 100];
const getDefaultPageSize = (fillHeight) => (fillHeight ? 100 : 10);

const PaginationActions = ({ className, onPageChange, page }) => {
  const gridRef = useGridApiContext();
  const count = useGridSelector(gridRef, gridPageCountSelector);

  return (
    <Pagination
      className={className}
      color="primary"
      count={count}
      page={page + 1}
      onChange={(event, value) => onPageChange(event, value - 1)}
      sx={styles.pagination}
    />
  );
};

const DataViewPagination = (props) => (
  <GridPagination
    ActionsComponent={PaginationActions}
    {...props}
    sx={styles.gridPagination}
  />
);

const Table = ({
  grid,
  columnOverrides = [],
  rowActions = [],
  loadGrid,
  onSelectionChange,
  fillHeight = false,
  sx,
  ...props
}) => {
  const [selection, setSelection] = useState([]);
  const [initialState, setInitialState] = useState(null);
  const keepSelectionOnNextDataChange = useRef(false);

  const handleLoadGrid = (payload) => {
    if (!loadGrid) {
      return;
    }

    if (payload?.staySelected) {
      keepSelectionOnNextDataChange.current = true;
    } else {
      setSelection([]);
    }

    loadGrid(payload);
  };

  useEffect(() => {
    handleLoadGrid(
      fillHeight ? { size: getDefaultPageSize(fillHeight) } : undefined
    );
  }, []);

  useEffect(() => {
    if (!grid?.colunas?.length) {
      return;
    }

    setInitialState({
      columns: {
        columnVisibilityModel: getColumnVisibilityModel(grid?.colunas),
      },
      sorting: {
        sortModel: getSortModel(grid?.order),
      },
    });
  }, [grid]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selection);
    }
  }, [onSelectionChange, selection]);

  useEffect(() => {
    if (keepSelectionOnNextDataChange.current) {
      keepSelectionOnNextDataChange.current = false;
      return;
    }

    setSelection([]);
  }, [grid?.data]);

  const columns = useMemo(() => {
    const actionColumn = rowActions?.length
      ? [
          {
            field: 'actions',
            headerName: 'Ações',
            type: 'actions',
            width: getRowActionsWidth(rowActions),
            getActions: (params) => RowActions({ actions: rowActions, params }),
          },
        ]
      : [];

    return mergeColumns(
      [...actionColumn, ...(grid?.colunas || [])],
      columnOverrides
    );
  }, [columnOverrides, grid?.colunas, rowActions]);

  if (!initialState) {
    return (
      <Box
        sx={{
          ...styles.loader,
          ...(fillHeight ? styles.loaderFill : {}),
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={fillHeight ? styles.tableFill : undefined}>
      <DataGrid
        {...props}
        sx={{
          ...(fillHeight ? styles.dataGridFill : {}),
          ...sx,
        }}
        initialState={initialState}
        rows={grid?.data || []}
        columns={columns}
        pagination
        paginationMode="server"
        autoHeight={!fillHeight}
        page={grid?.page || 0}
        rowCount={grid?.total || 0}
        pageSize={grid?.size || getDefaultPageSize(fillHeight)}
        rowsPerPageOptions={fillHeight ? pageSizeOptions : []}
        sortingMode="server"
        onPageChange={(page) =>
          handleLoadGrid({ page, staySelected: true })
        }
        onPageSizeChange={(size) => handleLoadGrid({ page: 0, size })}
        onSortModelChange={(sortModel) =>
          handleLoadGrid({ order: sortModel[0] })
        }
        selectionModel={selection?.map((item) => item?.id)}
        onSelectionModelChange={(ids) => {
          setSelection((previous) => {
            if (previous?.length > ids?.length) {
              return previous?.filter((item) =>
                ids?.some((id) => id === item?.id)
              );
            }

            const selectedRows = grid?.data?.filter(
              (row) =>
                ids?.some((id) => id === row?.id) &&
                !previous?.some((item) => item?.id === row?.id)
            );

            return [...previous, ...selectedRows];
          });
        }}
        components={{
          LoadingOverlay: LinearProgress,
          Pagination: DataViewPagination,
        }}
        density="compact"
        disableSelectionOnClick
        disableColumnMenu
        disableVirtualization={fillHeight}
        keepNonExistentRowsSelected
        showCellRightBorder
        showColumnRightBorder
        localeText={localeText}
      />
    </Box>
  );
};

export default Table;
